import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { catchError, delay, EMPTY, mergeMap, Observable, of, retry, retryWhen, take, throwError, timeout, TimeoutError, timer } from "rxjs";
import { environment } from "../../environments/environment";
import Bugsnag from "@bugsnag/js";
import { API_NAMESPACE, ApiRequestError, describeError, describeFailure, safeEndpoint } from "./log-sanitiser";

export interface Country {

	country_code: string;
	country: string;
}
export type Countries = Country[];

export interface Mover {
	id: number;
	name: string;
	logo: string | null;
	rating: string;
	rating_amount: number;
	city: string;
	checked: number;
	verified?: boolean;
}
export type Movers = Mover[];

@Injectable( {
	providedIn: 'root'
} )
export class ApiService {

	public domainData: any;
	public language: string;

	constructor( private http: HttpClient,
	             @Inject(LOCALE_ID) private locale: string) {

		// Take only the language part from the locale
		this.language = this.locale.split('-')[0];
	}

	private getPath() {

		return window.location.protocol + "//" + window.location.host + API_NAMESPACE;
	}

	/**
	 * Base URL for ERP API (verification etc.), no WordPress.
	 */
	private getErpPath(): string {

		const base = environment.erpApiBaseUrl ?? '';
		return base.endsWith( '/' ) ? base : base + '/';
	}

	private getErp( route: string ) {

		return this.http.get( this.getErpPath() + route )
			.pipe( retry( 2 ), catchError( this.handleError( route ) ) );
	}

	private postErp( route: string, body: any ) {

		return this.http.post<any>( this.getErpPath() + route, body )
			.pipe( retry( 2 ), timeout( 10000 ), catchError( this.handleError( route ) ) );
	}

	private get( route: string ) {

		return this.http.get( this.getPath() + route )
			.pipe( retry( 2 ), catchError( this.handleError( route ) ) );
	}

	private getPublic( url: string ) {

		return this.http.get( url )
			.pipe( retry( 2 ), catchError( this.handleError( url ) ) );
	}

	private post( route: string, body: any, retryCount: number = 0 ) {

		console.log( 'Posting to: ' + safeEndpoint( route ) );

		if( retryCount > 0 ) {

			// The 10s timeout below spans every attempt, so it usually fires before the
			// last attempt fails. Keep what the attempts returned, or the rejection would
			// say "timeout" for a backend that answered every time with a 502.
			let lastError: unknown = null;

			return this.http.post<any>( this.getPath() + route, body )
				.pipe(
					retryWhen(errors => {
						let retryAttempts = retryCount - 1;
						return errors.pipe(
							mergeMap(error => {

								lastError = error;

								if( retryAttempts > 0 ) {

									// Log the error without throwing until the final retry attempt
									console.log( '%cError during post. We will throw an actual error with the final retry. (' + retryAttempts + ' attempts left)', 'color: red' );
									console.log( 'Error during request:', describeError( error ) );

									retryAttempts--;
									// Delay between retries, you can adjust this if needed
									return of(error).pipe(delay(1000));
								}
								else {
									// On the final retry attempt, throw the error itself so the
									// catchError below can still say what went wrong
									return throwError( () => error );
								}
							}),
							take(retryCount)
						);
					}),
					// Max allowed is 10 seconds
					timeout(10000 ),
					catchError( error => {

						const failure = error instanceof TimeoutError && lastError !== null
							? 'timeout after ' + describeFailure( lastError )
							: describeFailure( error );

						console.error( 'Maximum retry attempts reached: ' + safeEndpoint( route ) + ' (' + failure + ')' );
						return throwError( () => new ApiRequestError( safeEndpoint( route ), failure ) );
					} )
			);
		}
		else {

			return this.http.post<any>( this.getPath() + route, body )
				.pipe( retry( retryCount ), timeout(10000), catchError( this.handleError( route ) ) );
		}
	}

	private postPublic( url: string, body: any, headers?: any ): Observable<any> {

		return this.http.post( url, body, { headers } );
	}

	/**
	 * The volume calculator is optional: a missing one must never make the quote form
	 * fail or reach the global error handler. getOptional() therefore completes quietly
	 * on failure, but still reports anything that is not the bot-generated 403 — see
	 * the note there.
	 *
	 * Only call this once the user has actually reached the movingContent step;
	 * MovingContentComponent is constructed on page load but stays hidden until then.
	 */
	public getVcItems(): Observable<any> {

		return this.getOptional( 'vc/' + this.language, 'Could not load the volume calculator items' );
	}

	public getVcPreItems( token: string ): Observable<any> {

		// It seems that we cache the API that this goes to. Added random parameter
		return this.getOptional(
			'vc-pre-items/' + token + '?nocache=' + (Math.random() + 1).toString(36).substring(7),
			'Could not load the pre-filled volume calculator items',
		);
	}

	private getOptional( route: string, failureMessage: string ): Observable<any> {

		return this.http.get( this.getPath() + route ).pipe(
			retry( {
				count: 2,
				delay: ( error: unknown ) => {

					// A crawler that honours our robots.txt refuses this request itself and
					// synthesises the 403, so retrying cannot change the outcome. Give up at
					// once there and keep the retries for genuine transport failures.
					if( error instanceof HttpErrorResponse && error.status === 403 ) {

						return throwError( () => error );
					}

					return timer( 400 );
				},
			} ),
			catchError( ( error: unknown ) => {

				// Report the endpoint name and status only, never the route or the raw
				// HttpErrorResponse: the vc-pre-items route embeds the visitor's _vcToken and
				// the error object repeats it in its `url`, and Bugsnag records console output
				// as breadcrumbs. Keeping identifiers out of here costs nothing diagnostically.
				const endpoint = safeEndpoint( route );
				const status = error instanceof HttpErrorResponse ? error.status : null;

				// Nothing here points at our backend, so reporting it would only build a new
				// noise group: 403 is a crawler refusing its own request against our
				// robots.txt, and 0 / 499 mean the request never completed on the client —
				// overwhelmingly the visitor navigating away mid-flight, which on a quote form
				// is ordinary behaviour rather than a fault.
				const clientSide = status === 403 || status === 0 || status === 499;

				if( clientSide ) {

					console.warn( failureMessage + ': ' + status + ' on ' + endpoint + ', request did not reach us' );
				}
				else {

					// A real outage on this endpoint must stay visible. Report it as a handled
					// error so we hear about it without the quote form failing over it.
					console.warn( failureMessage + ': ' + ( status ?? 'no response' ) + ' on ' + endpoint );

					Bugsnag.notify( new Error( failureMessage ), event => {

						event.addMetadata( 'request', { endpoint, status } );
					} );
				}

				return EMPTY;
			} ),
		);
	}

	public getCountriesFromAsset(): Observable<Countries> {

		return this.http.get<Countries>( 'assets/json/countries/' + this.language + '.json' )
			.pipe( retry( 2 ), timeout( 10000 ), catchError( this.handleError( 'countries-asset' ) ) );
	}

	public postRequest( body: any ): Observable<any> {

		// FormSessionInterceptor adds the X-Form-Session-Id correlation id
		// (DEV-6453/DEV-7864), so the WP plugin can attach it to the ERP call
		// and the backend request-validation log (DEV-6459) can join with our
		// pre-submission telemetry.
		return this.post( 'send-request', body, 10 );
	}

	public postQuestions( body: any ): Observable<any> {

		// Custom retry pipe (not via the generic post() helper) because the
		// thank-you redirect (~5s) and the dashboard button (sooner) cut the
		// effective retry window short — a 400ms delay fits more attempts
		// into 2-5s than the helper's 1s delay would.
		// Safe to retry now that ERP /submitquestion is concurrency-safe:
		// unique index on (re_id, qu_id) + once-only quota decrement (DEV-6540).
		return this.http.post<any>( this.getPath() + 'send-questions', body )
			.pipe(
				retry( { count: 9, delay: 400 } ),
				timeout( 10000 ),
				catchError( this.handleError( 'send-questions' ) ),
			);
	}

	/**
	 * DEV-6493: fire-and-forget affiliate-partner-form visit tracking. Posts the
	 * form's public token to the WP proxy, which forwards it to the ERP visit
	 * endpoint with the sirelotoken. Errors are swallowed — tracking must never
	 * affect form load or submission.
	 */
	public postFormVisit( formToken: string ): Observable<any> {

		return this.http.post<any>( this.getPath() + 'track-form-visit', { form_token: formToken } )
			.pipe(
				timeout( 10000 ),
				catchError( () => of( null ) ),
			);
	}

	public postVerifyOtp( token: string, otp: string ): Observable<any> {

		const route = 'verify-otp?id=' + token;
		// return this.postErp( route, { otp } );
		return this.post( route, { otp } );
	}

	public postResubmitPhoneForOtp( token: string, phone: string ): Observable<any> {

		const route = 'resubmit-phone-for-otp?id=' + token;
		// return this.postErp( route, { phone } );
		return this.post( route, { phone } );
	}

	public getResendOtp( token: string ): Observable<any> {

		const route = 'resend-otp?id=' + token;
		// return this.getErp( route );
		return this.get( route );
	}

	public postAskPrematch( body: any ): Observable<Movers> {

		return this.post( 'ask-prematch', body ).pipe(
			timeout(15000)
		);
	}

	public getPossibleMatches( body: any ): Observable<any> {

		return this.post( 'get-matches', body );
	}

	/**
	 * Optional like the volume calculator: without the customer the form only loses the
	 * mover prefill (DEV-7522). getOptional() keeps the bot 403s out of Bugsnag and
	 * safeEndpoint() keeps the customer key out of the report.
	 */
	public getCustomer( customerKey: string ): Observable<any> {

		return this.getOptional( 'get-customer?customer=' + customerKey, 'Could not load the customer' );
	}

	public getTestQuestions() : Observable<any> {

		return this.get( 'test-questions' );
	}

	public getPostCodeData( postcode: string ) : Observable<any> {

		// This api is locked behind a domain setting
		var apiKey = 'pi868TFXyFya6BBr';

		return this.getPublic( 'https://api.pro6pp.nl/v1/autocomplete?auth_key=' + apiKey + '&nl_sixpp=' + postcode );
	}

	public getZeroBounceResult( email: string ): Observable<any> {

		return this.get( 'validate-email?email=' + encodeURIComponent( email ) );
	}

	/**
	 * Upload a single image to tmp folder. Uses FormData; do not set Content-Type (browser sets it with boundary).
	 * Re-throws HttpErrorResponse so callers can read error.error?.error for user-facing messages.
	 */
	public uploadTmpImage( file: File, sessionId: string ): Observable<{ tmpPath: string; originalName: string }> {

		const formData = new FormData();
		formData.append( 'file', file );
		formData.append( 'session_id', sessionId );

		return this.http.post<{ tmpPath: string; originalName: string }>( this.getPath() + 'upload-tmp-image', formData )
			.pipe(
				timeout( 30000 ),
				catchError( ( err: HttpErrorResponse ) => {

					console.error( describeError( err ) );
					return throwError( () => err );
				} ),
			);
	}

	/**
	 * Delete a tmp image from the server. Fire-and-forget; used when user removes a photo from the form.
	 */
	public deleteTmpImage( tmpPath: string, sessionId: string ): Observable<{ deleted?: boolean }> {

		return this.http.request<{ deleted?: boolean }>( 'DELETE', this.getPath() + 'upload-tmp-image', {
			body: { tmpPath, session_id: sessionId },
		} ).pipe( timeout( 5000 ), catchError( this.handleError( 'upload-tmp-image' ) ) );
	}

	private handleError( route: string ) {

		// The endpoint comes from the route rather than the error: a TimeoutError carries
		// no URL, and timeouts are the failure we most need to tell apart.
		const endpoint = safeEndpoint( route );

		return ( error: unknown ) => {

			// Endpoint and failure only - see safeEndpoint(). The route, the raw error object
			// and the response body all carry identifiers, and every console call here ends
			// up in Bugsnag as a breadcrumb. describeError() owns the wording so there is a
			// single format to keep safe.
			//
			// There is deliberately no client-side-vs-server branch here. The obvious test,
			// `error.error instanceof ErrorEvent`, is dead on Angular 15 - HttpXhrBackend
			// builds the HttpErrorResponse from the DOM error / timeout / abort events,
			// which are ProgressEvents. And statusText adds nothing: Angular falls back to
			// the constant 'Unknown Error' for a transport failure, so appending it would
			// only repeat what `returned status 0` already says.
			//
			// Anything that is not an HttpErrorResponse - a TimeoutError above all - carries
			// no URL, so describeError() cannot name the endpoint for it. Prefix it here.
			console.error( error instanceof HttpErrorResponse ? describeError( error ) : endpoint + ': ' + describeError( error ) );

			// Reject with an Error that names the endpoint and the failure type, so a caller
			// that leaves it unhandled still gives Bugsnag something to triage (DEV-7562)
			return throwError( () => new ApiRequestError( endpoint, describeFailure( error ) ) );
		};
	}
}
